import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectPreview = ({ project, onRegisterClick }) => {
  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card border border-border rounded-xl p-8 shadow-sm relative overflow-hidden">
        {/* Premium Badge */}
        <div className="absolute top-4 right-4">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-warning/10 text-warning text-sm font-medium">
            <Icon name="Crown" size={14} className="mr-1" />
            Premium Project
          </div>
        </div>

        {/* Project Header */}
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-foreground mb-2">
            {project?.title}
          </h3>
          <div className="flex flex-wrap items-center gap-4 text-text-secondary">
            <div className="flex items-center">
              <Icon name="Tag" size={16} className="mr-2" />
              <span>{project?.category}</span>
            </div>
            <div className="flex items-center">
              <Icon name="MapPin" size={16} className="mr-2" />
              <span>{project?.location}</span>
            </div>
            <div className="flex items-center">
              <Icon name="Calendar" size={16} className="mr-2" />
              <span>Posted {formatDate(project?.postedDate)}</span>
            </div>
          </div>
        </div>

        {/* Project Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Timeline */}
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="flex items-center mb-2">
              <Icon name="Clock" size={18} className="mr-2 text-primary" />
              <h4 className="font-semibold text-foreground">Timeline</h4>
            </div>
            <p className="text-xl font-bold text-primary">{project?.timeline}</p>
          </div>

          {/* Response Deadline */}
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="flex items-center mb-2">
              <Icon name="AlertCircle" size={18} className="mr-2 text-warning" />
              <h4 className="font-semibold text-foreground">Deadline</h4>
            </div>
            <p className="text-xl font-bold text-warning">{formatDate(project?.responseDeadline)}</p>
          </div>
        </div>

        {/* Requirements Preview */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-foreground mb-4">Project Requirements</h4>
          <div className="space-y-3">
            {project?.requirements?.visible?.map((requirement, index) => (
              <div key={index} className="flex items-start">
                <Icon name="CheckCircle" size={16} className="mr-3 mt-1 text-success flex-shrink-0" />
                <span className="text-text-secondary">{requirement}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Locked Content Overlay */}
        <div className="relative">
          <div className="bg-gradient-to-t from-background via-background/80 to-transparent absolute inset-0 z-10 rounded-lg flex items-end justify-center pb-6">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-card border border-border shadow-sm mb-4">
                <Icon name="Lock" size={16} className="mr-2 text-warning" />
                <span className="text-sm font-medium">Additional details locked</span>
              </div>
              <Button
                variant="default"
                size="sm"
                iconName="Unlock"
                iconPosition="left"
                onClick={onRegisterClick}
              >
                Register to View Full Details
              </Button>
            </div>
          </div>

          {/* Blurred Hidden Content */}
          <div className="blur-sm pointer-events-none">
            <h4 className="text-lg font-semibold text-foreground mb-4">Additional Information</h4>
            <div className="space-y-3 mb-6">
              {project?.requirements?.hidden?.map((requirement, index) => (
                <div key={index} className="flex items-start">
                  <Icon name="CheckCircle" size={16} className="mr-3 mt-1 text-success flex-shrink-0" />
                  <span className="text-text-secondary">{requirement}</span>
                </div>
              ))}
            </div>

            {/* Client Info */}
            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <h5 className="font-semibold text-foreground mb-2">Client Information</h5>
              <p className="text-text-secondary">Name: {project?.clientName}</p>
              <p className="text-text-secondary">Contact details and project history available after registration</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="text-center p-4 bg-card border border-border rounded-lg">
          <div className="text-2xl font-bold text-primary mb-1">87%</div>
          <div className="text-sm text-text-secondary">Response Rate</div>
        </div>
        <div className="text-center p-4 bg-card border border-border rounded-lg">
          <div className="text-2xl font-bold text-success mb-1">4.8★</div>
          <div className="text-sm text-text-secondary">Client Rating</div>
        </div>
        <div className="text-center p-4 bg-card border border-border rounded-lg">
          <div className="text-2xl font-bold text-warning mb-1">12</div>
          <div className="text-sm text-text-secondary">Vendors Invited</div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPreview;